import type { FreelancerSearchResult } from "../types";

/**
 * Fotografia dell'ultima ricerca andata a buon fine (lista appiattita +
 * totale), mantenuta dalla route: `keepPreviousData` trattiene i dati vecchi
 * solo finché la nuova richiesta non fallisce — sull'errore `data` torna
 * `undefined` e senza questo snapshot il recruiter perderebbe una lista già
 * ottenuta per aver toccato un filtro (F-4, AC11).
 */
export type PreservedResults = {
  freelancers: FreelancerSearchResult[];
  total: number;
};

export type ResultsDisplayState =
  | "loading"
  | "error-empty"
  | "stale-preserved"
  | "live";

export type ResultsDisplayInput = {
  status: "pending" | "error" | "success";
  fetchStatus: "fetching" | "paused" | "idle";
  /** Dati esposti dalla query (freschi O placeholder di keepPreviousData). */
  hasData: boolean;
  /** Snapshot dell'ultima lista buona presente. */
  lastGood: boolean;
};

/**
 * Stato osservabile dell'area risultati (puro, testabile in node):
 *
 * - `live` — la query espone dati (freschi o placeholder): si rende quella.
 *   Raggiungibile SOLO con `hasData` vero: mai un ramo di default (era
 *   esattamente il BLOCKER — vedi sotto).
 * - `stale-preserved` — niente dati dalla query ma snapshot presente (errore
 *   su ricerca successiva, o retry in volo): lista conservata + etichetta,
 *   mai la loading screen (D12), mai "Carica altri" (E-12).
 * - `loading` — fetch in volo (`fetchStatus` "fetching" O "paused") e zero
 *   dati mostrabili: loading screen a step. "paused" copre la rete assente:
 *   con `networkMode: "online"` (default TanStack Query, mai sovrascritto in
 *   questo repo) una query senza rete non fallisce, resta `status: "pending"`
 *   / `fetchStatus: "paused"` finché la rete non torna. Prima del fix questo
 *   input cadeva sul `return "live"` finale — a valle "rendi i dati della
 *   query" con zero dati, area risultati muta per tutta l'assenza di rete.
 * - `error-empty` — `fetchStatus` "idle" (nessun fetch in volo) e niente da
 *   conservare: solo il banner. Vedi i branch commentati sotto per quali
 *   combinazioni di `status` ci arrivano davvero in produzione.
 */
export const resolveResultsDisplay = (
  input: ResultsDisplayInput,
): ResultsDisplayState => {
  if (input.hasData) return "live";
  if (input.lastGood) return "stale-preserved";
  if (input.fetchStatus === "fetching" || input.fetchStatus === "paused") {
    return "loading";
  }

  // Qui `fetchStatus` è "idle": nessun fetch in volo, zero dati esposti,
  // nessuno snapshot da preservare. Resta solo da capire cosa dice `status`:
  //
  // - "error" — l'unico caso che si osserva davvero in produzione: il fetch
  //   è concluso in errore e non c'è nulla da conservare (prima ricerca mai
  //   riuscita, o `lastGood` ancora vuoto). Solo il banner.
  // - "success" con `hasData` falso — difensivamente irraggiungibile: su
  //   `status: "success"` TanStack garantisce `data` definito, e l'unico
  //   consumatore (SearchResultsPanel) calcola `hasData` proprio come
  //   `!isPending && data !== undefined` — quindi "success" implica sempre
  //   `hasData: true`, intercettato dal primo branch.
  // - "pending" con `fetchStatus: "idle"` — anch'esso irraggiungibile con
  //   l'unico consumatore attuale: `useInfiniteSearchFreelancers` è sempre
  //   `enabled` in questo dominio (nessun `getEnabled` passato dalla route),
  //   e `placeholderData: keepPreviousData` promuove qualunque cache
  //   pregressa a "success" immediato appena cambiano i filtri. Un fetch
  //   abilitato senza cache parte "fetching" nello stesso istante, non resta
  //   mai fermo su "idle".
  //
  // Se in futuro un `getEnabled` condizionale introducesse davvero query
  // "pending"+"idle" (in attesa di una precondizione), "error-empty" resta
  // la scelta più onesta delle tre disponibili: non è "live" (zero dati),
  // non è "stale-preserved" (zero snapshot), e non è "loading" (nessun
  // fetch è realmente in volo — uno spinner qui mentirebbe a tempo
  // indeterminato). Mai muta, mai "live" senza dati.
  return "error-empty";
};
